import { useEffect, useSyncExternalStore } from 'react'
import { create } from 'zustand'

/**
 * Where the app-wide Toaster stacks its toasts.
 *
 * QA3 N9: toasts sit bottom-right by default, which is exactly where the
 * restaurant order screen pins its action bar (Send, Close & Bill, Void
 * order). An error toast covered Void order, so after a refusal the cashier
 * could not void until the toast went away. A screen that owns the
 * bottom-right corner claims it, and the toasts move out of its way:
 *   - md and up (768px+, the right-column cart): bottom-left, clear of the
 *     right column at 1280, 1024x768 and 768x1024;
 *   - below md the cart is a full-width bottom sheet, so the top.
 * Every other screen keeps bottom-right.
 */
export type ToastPosition = 'bottom-right' | 'bottom-left' | 'top-center'

/** Tailwind `md`: where the order screen switches to a right-column cart. */
export const WIDE_QUERY = '(min-width: 768px)'

export function toasterPosition(cornerClaimed: boolean, wide: boolean): ToastPosition {
  if (!cornerClaimed) return 'bottom-right'
  return wide ? 'bottom-left' : 'top-center'
}

interface ToastCornerStore {
  claims: number
  claim: () => void
  release: () => void
}

export const useToastCornerStore = create<ToastCornerStore>((set) => ({
  claims: 0,
  claim: () => set((s) => ({ claims: s.claims + 1 })),
  release: () => set((s) => ({ claims: Math.max(0, s.claims - 1) })),
}))

/** Call at the top of a screen whose action bar sits bottom-right; the
 * claim is released on unmount. A count, so nested claimants are safe. */
export function useClaimToastCorner(): void {
  const claim = useToastCornerStore((s) => s.claim)
  const release = useToastCornerStore((s) => s.release)
  useEffect(() => {
    claim()
    return () => release()
  }, [claim, release])
}

function subscribeWide(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined
  const mql = window.matchMedia(WIDE_QUERY)
  mql.addEventListener('change', onChange)
  return () => mql.removeEventListener('change', onChange)
}

function isWide(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true
  return window.matchMedia(WIDE_QUERY).matches
}

export function useToasterPosition(): ToastPosition {
  const claimed = useToastCornerStore((s) => s.claims > 0)
  const wide = useSyncExternalStore(subscribeWide, isWide, () => true)
  return toasterPosition(claimed, wide)
}
